import { ref } from 'vue'
import type { ChatAgentCp } from '@/composables/useAgentChatCp'
import type { DirectoryEntry, RecentInternalConversation } from '@/services/wootdashGoogleChat'

// Ctrl+Shift+C (App.vue) abre o popup de conversas da barra superior. Sem o Google Workspace configurado, o popup abre
// em modo exemplo (dados fictícios, com aviso) — pra mostrar como fica e aprovar antes de configurar.
const shortcutRequests = ref(0)

/** Aberto, minimizado (ícone no canto inferior esquerdo) ou fechado. Só o × fecha — clicar fora não. */
export type ChatPopupState = 'closed' | 'open' | 'minimized'
const state = ref<ChatPopupState>('closed')
/** Aberto como exemplo (sem o Google Workspace configurado) — continua exemplo ao minimizar e voltar. */
const exampleMode = ref(false)

export function toggleInternalChatPopupFromShortcut() {
  shortcutRequests.value++
}

export function minimizeInternalChatPopup() {
  if (state.value === 'open') state.value = 'minimized'
}

export function restoreInternalChatPopup() {
  if (state.value === 'minimized') state.value = 'open'
}

/** Abre a janela de verdade (não exemplo) — o "voltar" do chat com um agente cai aqui, onde estão todos os agentes. */
export function openInternalChatPopup() {
  exampleMode.value = false
  state.value = 'open'
}

export function useInternalChatPopup() {
  return { shortcutRequests, state, exampleMode }
}

/** O que o Ctrl+Shift+C faz em cada estado: fechado abre, aberto minimiza, minimizado volta. Nunca fecha (só o × fecha). */
export function shortcutAction(current: ChatPopupState): 'show' | 'minimize' | 'restore' {
  return current === 'closed' ? 'show' : current === 'open' ? 'minimize' : 'restore'
}

/** Exemplo quando o agente não tem o chat interno, ou quando nem a ponte nem o diretório do Google Workspace estão prontos. */
export function shouldOpenExample(canUseInternalChat: boolean, bridgeEnabled: boolean, directoryEnabled: boolean): boolean {
  return !canUseInternalChat || !(bridgeEnabled || directoryEnabled)
}

/** Dados fictícios do modo exemplo (com "Exemplo" no nome, pra ninguém confundir com gente de verdade). */
export function exampleData(now = Date.now()) {
  const minutesAgo = (m: number) => new Date(now - m * 60000).toISOString()
  const agents: ChatAgentCp[] = [
    { id: 'ex-1', name: 'Ana Exemplo', email: 'ana.exemplo@isbet.org.br', avatarUrl: '', availability: 'online' },
    { id: 'ex-2', name: 'Bruno Exemplo', email: 'bruno.exemplo@isbet.org.br', avatarUrl: '', availability: 'busy' },
    { id: 'ex-3', name: 'Carla Exemplo', email: 'carla.exemplo@isbet.org.br', avatarUrl: '', availability: 'offline' },
  ]
  const recent: RecentInternalConversation[] = [
    { peerAgentId: 'ex-1', peerName: 'Ana Exemplo', peerAvatarUrl: null, lastBody: 'Consegue olhar a fila do DF?', lastAt: minutesAgo(4), fromMe: false, unread: 2 },
    { peerAgentId: 'ex-2', peerName: 'Bruno Exemplo', peerAvatarUrl: null, lastBody: 'Retorno marcado pra amanhã às 9h.', lastAt: minutesAgo(52), fromMe: true, unread: 0 },
    { peerAgentId: 'ex-3', peerName: 'Carla Exemplo', peerAvatarUrl: null, lastBody: 'Obrigada!', lastAt: minutesAgo(60 * 26), fromMe: false, unread: 0 },
  ]
  const directory: DirectoryEntry[] = [
    { email: 'ana.exemplo@isbet.org.br', name: 'Ana Exemplo', photoUrl: null, agentId: 'ex-1' },
    { email: 'rh.exemplo@isbet.org.br', name: 'Ana do RH (exemplo)', photoUrl: null, agentId: null },
    { email: 'financeiro.exemplo@isbet.org.br', name: 'Bruno Financeiro (exemplo)', photoUrl: null, agentId: null },
  ]
  return { agents, recent, directory }
}
